import { useState } from "react";
import { useTranslation } from "react-i18next";

const fmtElapsed = (seconds, t) => {
  const s = Number(seconds);
  if (!Number.isFinite(s) || s <= 0) return null;
  const mins = Math.floor(s / 60);
  const secs = Math.round(s % 60);
  const secondLabel = t("learner.lms.quiz.secondsShort", "s");
  if (!mins) return `${secs}${secondLabel}`;
  return `${mins}${t("learner.lms.quiz.minutesShort", "m")} ${String(secs).padStart(2, "0")}${secondLabel}`;
};

const fmtTimestamp = (value, language) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  const time = d.toLocaleTimeString(language || "en-US", {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  });
  return `${day}/${month}/${year}, ${time}`;
};

const optionLetter = (index) => String.fromCharCode(65 + index);

const LmsQuizResultSummary = ({
  result,
  passMark,
  scorePercent,
  retake,
  loading,
  attemptsRemaining,
  attemptNumber,
  maxAttempts,
}) => {
  const { t, i18n } = useTranslation();
  const [showSolution, setShowSolution] = useState(false);
  const [solutionIndex, setSolutionIndex] = useState(0);

  const feedback = result?.feedback || [];
  const answersRevealed = result?.answersRevealed !== false;
  const correctCount = feedback.filter((f) => f.correct).length;
  const incorrectCount = Math.max(feedback.length - correctCount, 0);
  const currentSolution = feedback[solutionIndex] || feedback[0] || {};
  const solutionOptions = (currentSolution.options || []).map((option, originalIndex) => ({
    ...option,
    originalIndex,
  }));
  const finalScorePercent = Math.round(Number(scorePercent ?? result?.percent) || 0);
  const stroke = 339.292;
  const scoreOffset = stroke - (stroke * Math.min(finalScorePercent, 100)) / 100;
  const completedAt = fmtTimestamp(result?.completedAt, i18n.language);
  const timeTaken = fmtElapsed(result?.timeTakenSeconds, t);
  const canRetake = !result?.passed && (attemptsRemaining == null || attemptsRemaining > 0);
  const retryLabel =
    attemptsRemaining == null
      ? t("learner.lms.quiz.reattemptQuiz", "Reattempt Quiz")
      : t("learner.lms.quiz.reattemptsLeft", "Reattempt Quiz ({{count}} left)", {
        count: attemptsRemaining,
      });

  const openSolutionAt = (index) => {
    setSolutionIndex(index);
    setShowSolution(true);
  };

  const statusForOption = (option) => {
    if (option.isCorrect && option.isSelected) return "selected-correct";
    if (option.isSelected) return "selected-wrong";
    return "normal";
  };

  const labelForOption = (state) => {
    if (state === "selected-correct") {
      return t("learner.lms.quiz.yourCorrectAnswer", "Your Correct Answer");
    }
    if (state === "selected-wrong") {
      return t("learner.lms.quiz.yourIncorrectAnswer", "Your Incorrect Answer");
    }
    return "";
  };

  const iconForOption = (state) => {
    if (state === "selected-correct") return "fa-check";
    if (state === "selected-wrong") return "fa-times";
    return "";
  };

  const explanationTitleForOption = (state) => {
    if (state === "selected-correct") {
      return t("learner.lms.quiz.whyYourAnswerCorrect", "Why your answer is correct");
    }
    if (state === "selected-wrong") {
      return t("learner.lms.quiz.whyYourAnswerWrong", "Why your answer is wrong");
    }
    return "";
  };

  const shouldShowOptionExplanation = (option, state) =>
    !!option.explanation &&
    (state === "selected-correct" || state === "selected-wrong");

  return (
    <div className={`lms-preview-quiz lms-result-screen  bg-white ${result?.passed ? "pass" : "fail"}`}>
      <section className="lms-result-hero">
        <div className="lms-result-score-ring" style={{ "--score-offset": scoreOffset }}>
          <svg viewBox="0 0 120 120" aria-hidden="true">
            <circle className="lms-result-ring-track" cx="60" cy="60" r="54"></circle>
            <circle className="lms-result-ring-value" cx="60" cy="60" r="54"></circle>
          </svg>
          <div className="lms-result-ring-copy">
            <strong>{finalScorePercent}%</strong>
            <span>{t("learner.lms.quiz.score", "Score")}</span>
          </div>
        </div>

        <div className="lms-result-hero-copy">
          <span className="lms-result-status-pill">
            <i className={`fas ${result?.passed ? "fa-circle-check" : "fa-circle-xmark"}`}></i>
            {result?.passed
              ? t("learner.lms.quiz.passed", "Passed")
              : result?.timedOut
                ? t("learner.lms.quiz.timedOut", "Time ran out")
                : t("learner.lms.quiz.notPassed", "Not Passed")}
          </span>
          <h3>
            {result?.passed
              ? t("learner.lms.quiz.passResultTitle", "Assessment passed")
              : t("learner.lms.quiz.failResultTitle", "Assessment not passed")}
          </h3>
          <p>
            {result?.passed
              ? t(
                "learner.lms.quiz.passResultBody",
                "Great work. You have met the required passing percentage."
              )
              : canRetake
                ? answersRevealed
                  ? t("learner.lms.quiz.failResultBody", "Review the solutions, then reattempt the quiz when you are ready.")
                  : t("learner.lms.quiz.failResultWithoutSolutions", "Review your result, then reattempt the quiz when you are ready.")
                : answersRevealed
                  ? t("learner.lms.quiz.noReattemptsLeft", "Review the solutions. No reattempts are left for this quiz.")
                  : t("learner.lms.quiz.noReattemptsWithoutSolutions", "No reattempts are left for this quiz.")}
          </p>
          <div className="lms-result-meta">
            <span>
              <i className="fas fa-bullseye"></i>
              {t("learner.lms.quiz.passPercentage", "Passing percentage {{mark}}%", {
                mark: passMark,
              })}
            </span>
            {result?.marksScored != null && result?.totalMarks != null && (
              <span>
                <i className="fas fa-award"></i>
                {t("learner.lms.quiz.marksScored", "Marks {{score}}/{{total}}", {
                  score: result.marksScored,
                  total: result.totalMarks,
                })}
              </span>
            )}
            {attemptNumber != null && (
              <span>
                <i className="fas fa-rotate-right"></i>
                {maxAttempts
                  ? t("learner.lms.quiz.attemptNOfTotal", "Attempt {{n}}/{{total}}", {
                    n: attemptNumber,
                    total: maxAttempts,
                  })
                  : t("learner.lms.quiz.attemptN", "Attempt {{n}}", {
                    n: attemptNumber,
                  })}
              </span>
            )}
            {completedAt && (
              <span>
                <i className="fas fa-calendar-check"></i>
                {completedAt}
              </span>
            )}
          </div>
        </div>
      </section>

      <section className="lms-result-stats" aria-label={t("learner.lms.quiz.performance", "Quiz performance")}>
        <div className="lms-result-stat-card correct">
          <span><i className="fas fa-check"></i></span>
          <strong>{correctCount}</strong>
          <em>{t("learner.lms.quiz.correctAnswers", "Correct Answers")}</em>
        </div>
        <div className="lms-result-stat-card incorrect">
          <span><i className="fas fa-times"></i></span>
          <strong>{incorrectCount}</strong>
          <em>{t("learner.lms.quiz.incorrectAnswers", "Incorrect Answers")}</em>
        </div>
        {timeTaken && (
          <div className="lms-result-stat-card time">
            <span><i className="fas fa-clock"></i></span>
            <strong>{timeTaken}</strong>
            <em>{t("learner.lms.quiz.timeTaken", "Time Taken")}</em>
          </div>
        )}
        <div className="lms-result-stat-card total">
          <span><i className="fas fa-clipboard-list"></i></span>
          <strong>{feedback.length}</strong>
          <em>{t("learner.lms.quiz.totalQuestions", "Total Questions")}</em>
        </div>
      </section>

      <section className="lms-result-review-card">
        <div className="lms-result-review-head">
          <div>
            <h4>{t("learner.lms.quiz.questionSummary", "Question Summary")}</h4>
            <p>{t("learner.lms.quiz.questionSummaryHint", "Select any question to view its result.")}</p>
          </div>
          <div className="lms-result-legend">
            <span><i className="correct"></i>{t("learner.lms.quiz.correct", "Correct")}</span>
            <span><i className="incorrect"></i>{t("learner.lms.quiz.incorrect", "Incorrect")}</span>
            <span><i className="skipped"></i>{t("learner.lms.quiz.notAttempted", "Not Attempted")}</span>
          </div>
        </div>

        <div className="lms-result-question-grid">
          {feedback.map((f, i) => {
            const state = !f.selectedOptionIds?.length
              ? "skipped"
              : f.correct
                ? "correct"
                : "incorrect";
            return (
              <button
                className={`lms-result-question-chip ${state}`}
                key={f.questionId || i}
                onClick={() => openSolutionAt(i)}
                type="button"
              >
                <span>{i + 1}</span>
                <strong>
                  {state === "correct"
                    ? t("learner.lms.quiz.correct", "Correct")
                    : state === "incorrect"
                      ? t("learner.lms.quiz.incorrect", "Incorrect")
                      : t("learner.lms.quiz.notAttempted", "Not Attempted")}
                </strong>
                <i className="fas fa-chevron-right"></i>
              </button>
            );
          })}
        </div>

        <div className="lms-result-actions">
          {canRetake && (
            <button
              className="lms-result-action-primary"
              onClick={retake}
              type="button"
              disabled={loading}
            >
              <i className="fas fa-rotate-right"></i>
              {retryLabel}
            </button>
          )}
          <button
            className="lms-result-action-secondary"
            onClick={() => openSolutionAt(0)}
            type="button"
            disabled={!feedback.length}
          >
            <i className="fas fa-list-check"></i>
            {answersRevealed
              ? t("learner.lms.quiz.viewSolutions", "View Solutions")
              : t("learner.lms.quiz.reviewAnswers", "Review Answers")}
          </button>
        </div>
      </section>

      {showSolution && (
        <div className="lms-solution-modal-backdrop" role="presentation">
          <div
            className="lms-solution-modal-scrim"
            onClick={() => setShowSolution(false)}
          ></div>
          <button
            className="lms-solution-float-nav previous"
            disabled={solutionIndex === 0}
            onClick={() => setSolutionIndex((i) => Math.max(0, i - 1))}
            type="button"
            aria-label={t("learner.lms.player.previous", "Previous")}
          >
            <i className="fas fa-chevron-left"></i>
          </button>
          <div className="lms-solution-modal" role="dialog" aria-modal="true">
            <header className="lms-solution-header">
              <div>
                
                <span>
                  {t("learner.lms.quiz.questionOf", "Question {{n}} of {{total}}", {
                    n: solutionIndex + 1,
                    total: feedback.length,
                  })}
                </span>
              </div>
              <h3 className="text-center"
                >{t("learner.lms.quiz.viewSolution", "View Solution")}</h3>
              <button
                className="lms-solution-close"
                onClick={() => setShowSolution(false)}
                type="button"
                aria-label={t("common.close", "Close")}
              >
                <i className="fas fa-times"></i>
              </button>
            </header>

            <div className="lms-solution-content" key={solutionIndex}>
              <section className="lms-solution-question">
                <span className="lms-solution-question-number">{solutionIndex + 1}</span>
                <p>{currentSolution.questionText}</p>
              </section>

              <section className="lms-solution-options">
                {!solutionOptions.some((option) => option.isSelected) && (
                  <div className="lms-solution-option skipped">
                    <span className="lms-solution-option-index">
                      <i className="fas fa-minus"></i>
                    </span>
                    <div className="lms-solution-option-body">
                      <span className="lms-solution-option-text">
                        {t("learner.lms.quiz.noOptionSelected", "No option was selected for this question.")}
                      </span>
                    </div>
                  </div>
                )}
                {solutionOptions.map((option) => {
                  const state = statusForOption(option);
                  const label = labelForOption(state);
                  const icon = iconForOption(state);
                  const explanationTitle = explanationTitleForOption(state);
                  const showExplanation = shouldShowOptionExplanation(option, state);
                  return (
                    <div className={`lms-solution-option ${state}`} key={option.id}>
                      <span className="lms-solution-option-index">
                        {optionLetter(option.originalIndex)}
                      </span>
                      <div className="lms-solution-option-body">
                        <span className="lms-solution-option-text">{option.text}</span>
                        {showExplanation && (
                          <div className="lms-solution-option-explanation">
                            <strong>{explanationTitle}</strong>
                            <p>{option.explanation}</p>
                          </div>
                        )}
                      </div>
                      {label && (
                        <span className="lms-solution-option-badge">
                          <i className={`fas ${icon}`}></i>
                          {label}
                        </span>
                      )}
                    </div>
                  );
                })}
              </section>
            </div>

            <footer className="lms-solution-footer">
              <div className="lms-solution-question-nav">
                {feedback.map((f, i) => (
                  <button
                    className={`lms-solution-question-dot ${!f.selectedOptionIds?.length
                      ? "skipped"
                      : f.correct
                        ? "correct"
                        : "incorrect"
                      } ${i === solutionIndex ? "current" : ""}
                      ${
        feedback.length === 1 ? "not-clickable" : ""
      }
                      `}
                    key={f.questionId || i}
                    onClick={() => setSolutionIndex(i)}
                    type="button"
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
            </footer>
          </div>
          <button
            className="lms-solution-float-nav next"
            disabled={solutionIndex >= feedback.length - 1}
            onClick={() =>
              setSolutionIndex((i) => Math.min(feedback.length - 1, i + 1))
            }
            type="button"
            aria-label={t("learner.lms.player.next", "Next")}
          >
            <i className="fas fa-chevron-right"></i>
          </button>
        </div>
      )}
    </div>
  );
};

export default LmsQuizResultSummary;
